import React, { useState, useEffect, useCallback } from 'react';
import {
  BlockElement,
  Replacements,
  ReplacementValue,
  BlockReplacement as BlockReplacementType,
  BlockRendererProps as Props,
  BulletListBlock as BulletListBlockType,
  OrderedListBlock as OrderedListBlockType,
  ListItemBlock as ListItemBlockType,
  TextBlock as TextBlockType,
  SectionBlock as SectionBlockType,
  CalloutBlock as CalloutBlockType,
  IconBlock as IconBlockType
} from './types';
import { BlockReplacement } from './BlockElements/BlockReplacement';
import { useBlockKit } from './context/BlockKitContext';
import {
  TextBlock,
  HeadingBlock,
  SectionBlock,
  StepListBlock,
  BulletListBlock,
  OrderedListBlock,
  AlertBlock,
  WizardBlock,
  StepBlock,
  WizardStepBlock,
  CalloutBlock,
  IconBlock
} from './BlockElements';

/**
 * Type guard to check if a value is a valid replacement value for text formatting
 * @param value - The value to check
 * @returns True if the value is a valid replacement value
 */
const isValidTextReplacement = (value: unknown): boolean => {
  return value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    React.isValidElement(value);
};

/**
 * Type guard to check if a value is a BlockElement
 * @param value - The value to check
 * @returns True if the value is a BlockElement
 */
const isBlockElement = (value: unknown): value is BlockElement => {
  return typeof value === 'object' && value !== null && 'type' in value;
};

const processBlock = (block: BlockElement, replacements: Replacements = {}): BlockElement => {
  if (!block) return block;

  // For replacement blocks, just pass them through
  // They will be handled by the BlockReplacement component in renderBlock
  if (block.type === 'block_replacement') {
    return block;
  }

  // Handle list blocks (bullet or ordered)
  if ((block.type === 'bullet_list' || block.type === 'ordered_list') && 'items' in block && Array.isArray(block.items)) {
    // Include all valid replacements including React elements
    const filteredReplacements = Object.entries({
      ...(block.replacements || {}),
      ...replacements
    }).reduce<Replacements>(
      (acc, [key, value]) => {
        if (isValidTextReplacement(value)) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    // Process each list item and store the global replacements at the block level
    const processedBlock = {
      ...block,
      // Store filtered replacements at the list block level
      replacements: filteredReplacements,
      items: block.items.map((item: any) => {
        // If the item is a string, it's a reference to a replacement
        if (typeof item === 'string' && item.startsWith('{') && item.endsWith('}')) {
          const replacementKey = item.slice(1, -1);
          const replacement = replacements[replacementKey];

          if (Array.isArray(replacement)) {
            // If replacement is an array, map each item to a list item
            return {
              type: 'list_item',
              text: String(replacement.join(', '))
            } as ListItemBlockType;
          }

          // If it's a single value, use it as the text
          return {
            type: 'list_item',
            text: String(replacement ?? '')
          } as ListItemBlockType;
        }

        // For text in list items, we need to make sure they have access to the global replacements
        if (item.type === 'list_item' && typeof item.text === 'string') {
          return {
            ...item,
            // We don't add item-specific replacements since they don't exist
          };
        }

        // Process the item normally
        return processBlock(item, replacements) as ListItemBlockType;
      })
    };

    return processedBlock as BulletListBlockType | OrderedListBlockType;
  }

  // Process nested elements
  if ('elements' in block && block.elements) {
    return {
      ...block,
      elements: block.elements.map(el => processBlock(el, replacements))
    } as BlockElement;
  }

  // Process text blocks with replacements
  if (block.type === 'text' && 'text' in block) {
    const textBlock = block as TextBlockType;

    // Include all valid replacements including React elements
    const filteredReplacements = Object.entries({
      ...(textBlock.replacements || {}),
      ...replacements
    }).reduce<Replacements>(
      (acc, [key, value]) => {
        if (isValidTextReplacement(value)) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    return {
      ...textBlock,
      replacements: filteredReplacements,
      as: textBlock.as,
      textAlign: textBlock.textAlign
    } as TextBlockType;
  }

  // Process alert blocks with replacements
  if (block.type === 'alert' && 'text' in block) {
    const alertBlock = block as any; // AlertBlock type

    // Include all valid replacements including React elements
    const filteredReplacements = Object.entries({
      ...(alertBlock.replacements || {}),
      ...replacements
    }).reduce<Replacements>(
      (acc, [key, value]) => {
        if (isValidTextReplacement(value)) {
          acc[key] = value as ReplacementValue;
        }
        return acc;
      },
      {}
    );

    return {
      ...alertBlock,
      replacements: filteredReplacements
    };
  }

  return block;
};

/**
 * BlockRenderer component renders a list of block elements with optional replacements
 * 
 * @param {Props} props - Component props
 * @returns {React.ReactElement} Rendered blocks
 */
const BlockRenderer: React.FC<Props> = ({
  blocks,
  replacements: propReplacements,
  onWizardComplete,
  onWizardStepChange,
  onRenderStart,
  onRenderEnd
}) => {
  // Call onRenderStart when component starts rendering
  useEffect(() => {
    onRenderStart?.();

    // Call onRenderEnd after initial render and any updates
    const timer = setTimeout(() => {
      onRenderEnd?.();
    }, 0);

    return () => clearTimeout(timer);
  }, [onRenderStart, onRenderEnd]);

  // Handle wizard completion
  const handleWizardComplete = useCallback(() => {
    onWizardComplete?.();
  }, [onWizardComplete]);

  // Handle wizard step changes
  const handleWizardStepChange = useCallback((previousStep: number | undefined, currentStep: number) => {
    onWizardStepChange?.(previousStep, currentStep);
  }, [onWizardStepChange]);


  if (!blocks) {
    console.error('BlockRenderer: blocks prop is required');
    return null;
  }

  // Handle both arrays and builder objects
  let blocksToRender: BlockElement[] = [];

  if (Array.isArray(blocks)) {
    blocksToRender = blocks;
  } else if (typeof blocks === 'object' && blocks !== null) {
    // Check if it's a builder object with a build method
    if (typeof (blocks as any).build === 'function') {
      const built = (blocks as any).build();
      blocksToRender = Array.isArray(built) ? built : [built];
    } else {
      // It's a single block object
      blocksToRender = [blocks as BlockElement];
    }
  } else {
    console.error('BlockRenderer: blocks prop must be an array or a valid block object. Received:', blocks);
    return null;
  }


  // Get the block kit context
  const { replacements: contextReplacements } = useBlockKit();

  // Merge prop replacements with context replacements, with prop replacements taking precedence
  const mergedReplacements = React.useMemo(() => {
    return {
      ...contextReplacements,
      ...(propReplacements || {})
    };
  }, [contextReplacements, propReplacements]);

  // Process all blocks with replacements
  const processedBlocks = React.useMemo(() => {
    // Helper function to recursively check for block_replacement blocks
    const findBlockReplacements = (block: BlockElement): boolean => {
      if (!block) return false;

      if (block.type === 'block_replacement') {
        return true;
      }

      // Check for elements in various block types
      if ('elements' in block && Array.isArray(block.elements)) {
        return block.elements.some(el => findBlockReplacements(el));
      }

      // Check for wizard steps
      if (block.type === 'wizard' && 'elements' in block && Array.isArray(block.elements)) {
        return block.elements.some(step => {
          if (step.type === 'wizard_step' && 'elements' in step && Array.isArray(step.elements)) {
            return step.elements.some(el => findBlockReplacements(el));
          }
          return false;
        });
      }

      return false;
    };

    // Check if any block_replacement blocks exist
    const hasReplacementBlocks = blocksToRender.some(block => findBlockReplacements(block));

    return blocksToRender.map(block => processBlock(block, mergedReplacements));
  }, [blocksToRender, mergedReplacements]);

  // Use the merged replacements for all blocks
  const effectiveReplacements = mergedReplacements;

  // Helper function to render a specific block type
  const renderBlock = (block: BlockElement, key: string) => {
    if (!block) return null;

    // Create element with key
    const withKey = (element: React.ReactNode) => {
      return element ? React.createElement(React.Fragment, { key }, element) : null;
    };

    switch (block.type) {
      case 'text':
        return withKey(TextBlock({ block: block as TextBlockType }));
      case 'heading':
        return withKey(HeadingBlock({ block }));
      case 'section':
        return withKey(SectionBlock({ block: block as SectionBlockType }));
      case 'step_list':
        return withKey(StepListBlock({ block }));
      case 'bullet_list':
        // Special handling for bullet lists
        if (!block.items || block.items.length === 0) {
          return null;
        }
        return withKey(BulletListBlock({ block: block as BulletListBlockType }));
      case 'ordered_list':
        // Special handling for ordered lists
        if (!block.items || block.items.length === 0) {
          return null;
        }
        return withKey(OrderedListBlock({ block: block as OrderedListBlockType }));
      case 'alert':
        return withKey(AlertBlock({ block }));
      case 'wizard':
        return withKey(WizardBlock({ block, onWizardComplete }));
      case 'block_replacement':
        return withKey(BlockReplacement({
          block: block as BlockReplacementType
        }));
      case 'step':
        // StepBlock is rendered by StepListBlock with step numbers
        return null;
      case 'list_item':
        // ListItem blocks are rendered by BulletListBlock or OrderedListBlock
        return null;
      case 'wizard_step':
        // Wizard step blocks are rendered by WizardBlock
        return null;
      case 'callout':
        return withKey(CalloutBlock({ block: block as CalloutBlockType }));
      case 'icon':
        return withKey(IconBlock({ block: block as IconBlockType }));
      default:
        if (process.env['NODE_ENV'] !== 'production') {
          console.warn(`Unknown block type: ${(block as any).type}`);
        }
        return null;
    }
  };

  // Render the blocks
  return (
    <>
      {processedBlocks.map((block, index) => (
        renderBlock(block, `block-${index}`)
      ))}
    </>
  );
};

export default BlockRenderer;
